// v3 scoring — decoupled from the legacy scoringEngine.ts (5-dim dashboard).
//
// The Notion "model v2" quiz bakes per-answer points into each option/statement:
//   single_choice option → { scores: { dims, patterns } }
//   scale_group statement → { scores: { "1": {dims,patterns}, "2":…, "4":…, "5":… } }  ("3" = neutral)
// Scoring reads the flat userProfile (keyed by each question's Notion Code ID) and
// the quiz pages, and sums dimension + pattern points. Bipolar: agreement with a
// shadow statement pushes its dimension negative; the more negative a dimension,
// the more it needs work.
import { QuizData, QuizPage, V3Scores } from '../types/quiz';
import { ScoringResult } from './scoringEngine';
import { computeRecommendedTrack, TRACK_CODES } from './trackRouting';

export interface V3Totals {
    dims: Record<string, number>;
    patterns: Record<string, number>;
}

/** Lowest and highest total a key could reach given the questions actually asked. */
export interface V3Range {
    min: number;
    max: number;
}

export interface V3Ranges {
    dims: Record<string, V3Range>;
    patterns: Record<string, V3Range>;
}

export interface V3Result extends V3Totals {
    top_patterns: Array<{ id: string; score: number }>;
    recommended_track: string | null;
    selected_track: string | null;
    selected_patterns: string[];
}

const addBag = (target: Record<string, number>, bag: Record<string, number> | undefined, weight = 1): void => {
    if (!bag) return;
    Object.entries(bag).forEach(([k, v]) => {
        if (typeof v === 'number') target[k] = (target[k] || 0) + v * weight;
    });
};

const answered = (v: unknown): boolean => v !== undefined && v !== null && v !== '';

// Add one page's contribution to running dims/patterns totals from the flat profile.
export const accumulatePage = (
    page: QuizPage,
    profile: Record<string, any>,
    totals: V3Totals,
    opts: { includePatterns?: boolean; routingOnly?: boolean } = {},
): void => {
    const includePatterns = opts.includePatterns !== false;

    if (page.type === 'single_choice' && Array.isArray((page as any).options)) {
        if (opts.routingOnly && !(page as any).routing) return;
        const field = page.data_field;

        if (!field) return;
        const val = profile[field];

        if (!answered(val)) return;
        const opt = (page as any).options.find((o: any) => o.value === val || o.id === val);
        const scores: V3Scores | undefined = opt && opt.scores;

        if (!scores) return;
        addBag(totals.dims, scores.dims);
        if (includePatterns) addBag(totals.patterns, scores.patterns);
        return;
    }

    if (page.type === 'scale_group' && Array.isArray((page as any).statements)) {
        if (opts.routingOnly && !(page as any).routing) return;
        (page as any).statements.forEach((st: any) => {
            const val = profile[st.data_field];

            if (!answered(val)) return;
            const level: V3Scores | undefined = st.scores && st.scores[String(val)];

            if (!level) return; // "3" neutral or an unscored answer
            addBag(totals.dims, level.dims);
            if (includePatterns) addBag(totals.patterns, level.patterns);
        });
    }
};

// Sum dims + patterns across every scoring page (routing + track), from the flat profile.
export const accumulateScores = (pages: QuizPage[], profile: Record<string, any>): V3Totals => {
    const totals: V3Totals = { dims: {}, patterns: {} };

    (pages || []).forEach((page) => accumulatePage(page, profile, totals));
    return totals;
};

/**
 * Best- and worst-case totals per key over the pages the user was actually
 * shown. This is the v3 counterpart of the legacy engine's `computeMaxScores`,
 * with one difference: v3 points are SIGNED (agreeing with a shadow statement
 * pushes its track negative), so a key runs from `min` to `max`, not 0 to max.
 *
 * Every question contributes exactly one answer, so per question the candidate
 * set is "what each selectable answer would award" — including 0 for answers
 * that award nothing (and for the neutral "3" on a scale_group).
 *
 * Pass ONLY the pages that were presented. The v3 quiz asks the routing block
 * plus a single track, so feeding it all eight track files would invent
 * headroom for questions the user never saw.
 *
 * `profile` narrows it further to the questions that actually have an answer.
 * A top-up pass through `/quiz-extended` answers a track's questions without
 * re-answering the routing block, so counting routing's headroom would drag
 * that track's percent toward the middle. Note that a neutral "3" IS an answer
 * — it scores nothing but still belongs in the range.
 */
export const computeV3Ranges = (pages: QuizPage[], profile?: Record<string, any>): V3Ranges => {
    const ranges: V3Ranges = { dims: {}, patterns: {} };
    const wasAnswered = (field: string | undefined): boolean =>
        !profile || (!!field && answered(profile[field]));

    const fold = (bucket: Record<string, V3Range>, candidates: Array<Record<string, number> | undefined>): void => {
        const keys = new Set<string>();

        candidates.forEach((c) => Object.keys(c || {}).forEach((k) => keys.add(k)));
        keys.forEach((key) => {
            const values = candidates.map((c) => (c && typeof c[key] === 'number' ? c[key] : 0));
            const prev = bucket[key] || { min: 0, max: 0 };

            bucket[key] = {
                min: prev.min + Math.min(...values),
                max: prev.max + Math.max(...values),
            };
        });
    };

    (pages || []).forEach((page) => {
        if (page.type === 'single_choice' && Array.isArray((page as any).options)) {
            const opts = (page as any).options as Array<{ scores?: V3Scores }>;

            if (!page.data_field || !opts.some((o) => o.scores)) return;
            if (!wasAnswered(page.data_field)) return;
            fold(
                ranges.dims,
                opts.map((o) => o.scores?.dims),
            );
            fold(
                ranges.patterns,
                opts.map((o) => o.scores?.patterns),
            );
            return;
        }

        if (page.type === 'scale_group' && Array.isArray((page as any).statements)) {
            (page as any).statements.forEach((st: any) => {
                if (!st.scores) return;
                if (!wasAnswered(st.data_field)) return;
                // "3" is neutral and carries no entry — represent it as the
                // undefined candidate so it folds in as a zero.
                const levels: Array<V3Scores | undefined> = ['1', '2', '3', '4', '5'].map((k) => st.scores[k]);

                fold(
                    ranges.dims,
                    levels.map((l) => l?.dims),
                );
                fold(
                    ranges.patterns,
                    levels.map((l) => l?.patterns),
                );
            });
        }
    });

    return ranges;
};

/** Where `value` sits in [min, max], as 0–100. Returns null if nothing was asked. */
const rangePercent = (value: number, range: V3Range | undefined): number | null => {
    if (!range) return null;
    const span = range.max - range.min;

    if (span <= 0) return null;
    return Math.round(Math.max(0, Math.min(1, (value - range.min) / span)) * 100);
};

/**
 * Adapt a v3 result to the `ScoringResult` the editorial dashboard renders.
 *
 * Track percent reads as health: 100 = every answer pointed the healthy way,
 * 0 = every answer pointed at the constraint. Pattern percent reads as
 * intensity: 100 = the pattern was fully endorsed.
 *
 * `pendulums` is empty — pendulums are retired in this model.
 */
export const v3ToScoringResult = (
    totals: V3Totals,
    ranges: V3Ranges,
    tagTypes: Record<string, 'shadow' | 'engine'>,
): ScoringResult => {
    const dimensions: ScoringResult['dimensions'] = {};

    Object.keys(ranges.dims).forEach((id) => {
        const raw = totals.dims[id] || 0;
        const percent = rangePercent(raw, ranges.dims[id]);

        if (percent === null) return;
        dimensions[id] = { raw, normalized: percent / 100, percent };
    });

    const tags: ScoringResult['tags'] = Object.keys(ranges.patterns)
        .map((id) => {
            const raw = totals.patterns[id] || 0;
            const percent = rangePercent(raw, ranges.patterns[id]) ?? 0;

            return { id, raw, normalized: percent / 100, percent, type: tagTypes[id] || ('shadow' as const) };
        })
        .sort((a, b) => b.normalized - a.normalized);

    return { dimensions, tags, pendulums: [] };
};

/** One completed pass: a track, the answers given, and the pages that were asked. */
export interface TrackSubmission {
    track: string;
    answers: Record<string, any>;
    /** The shell (routing) pages plus that track's pages, in the order asked. */
    pages: QuizPage[];
}

export interface MergedResult {
    scores: ScoringResult;
    /** Tracks with a completed pass, in the order supplied. */
    measuredTracks: string[];
}

/**
 * Fold every completed pass into ONE dashboard result.
 *
 * A pass measures exactly one track in depth, so only that track's score is
 * taken from it. The routing block does touch all eight tracks, but five
 * screening questions spread across eight domains is a recommendation signal,
 * not a measurement — reporting a percent off it would dress up a guess as a
 * result. Unmeasured tracks are therefore absent from `scores.dimensions`
 * entirely, which is what lets the dashboard render them as "not taken yet"
 * instead of a misleading 0%.
 *
 * Patterns follow their track: only patterns owned by a measured track are
 * scored, for the same reason.
 *
 * Callers must de-duplicate by track first (newest pass per track wins).
 */
export const mergeTrackResults = (
    submissions: TrackSubmission[],
    tagTypes: Record<string, 'shadow' | 'engine'>,
    tagDimension: Record<string, string>,
): MergedResult => {
    const dimensions: ScoringResult['dimensions'] = {};
    const tags: ScoringResult['tags'] = [];
    const measuredTracks: string[] = [];

    submissions.forEach(({ track, answers, pages }) => {
        if (!track || dimensions[track]) return;
        // Scope the range to what this pass actually answered — a top-up pass
        // skips the routing block, so its headroom must not count.
        const ranges = computeV3Ranges(pages, answers);
        const totals = accumulateScores(pages, answers);
        const percent = rangePercent(totals.dims[track] || 0, ranges.dims[track]);

        if (percent === null) return;
        measuredTracks.push(track);
        dimensions[track] = { raw: totals.dims[track] || 0, normalized: percent / 100, percent };

        Object.keys(ranges.patterns).forEach((id) => {
            if (tagDimension[id] !== track) return;
            const raw = totals.patterns[id] || 0;
            const pct = rangePercent(raw, ranges.patterns[id]);

            if (pct === null) return;
            tags.push({ id, raw, normalized: pct / 100, percent: pct, type: tagTypes[id] || 'shadow' });
        });
    });

    tags.sort((a, b) => b.normalized - a.normalized);
    return { scores: { dimensions, tags, pendulums: [] }, measuredTracks };
};

// Full v3 result block — used for persistence (_scores_v3) and the dashboard.
export const computeV3Result = (profile: Record<string, any>, quizData: QuizData | null): V3Result => {
    const pages = (quizData && quizData.pages) || [];
    const { dims, patterns } = accumulateScores(pages, profile);
    const trackCodes = quizData && quizData.tracks ? Object.keys(quizData.tracks) : [...TRACK_CODES];
    const { recommendedTrack } = computeRecommendedTrack(profile, pages, trackCodes);

    const top_patterns = Object.entries(patterns)
        .map(([id, score]) => ({ id, score }))
        .sort((a, b) => b.score - a.score);

    const selRaw = profile.selected_pattern;
    let selected_patterns: string[] = [];

    if (Array.isArray(selRaw)) selected_patterns = selRaw as string[];
    else if (selRaw) selected_patterns = [selRaw as string];

    return {
        dims,
        patterns,
        top_patterns,
        recommended_track: (profile.recommended_track as string) || recommendedTrack,
        selected_track: (profile.selected_track as string) || null,
        selected_patterns,
    };
};
